import * as React from 'react';
import type { ModalState } from '../hooks/useModalState';

/**
 * Application settings context for managing global configuration and modal state.
 *
 * **TESTING GUIDANCE** (MoSCoW Priority: SHOULD):
 * This context and its hooks SHOULD be used directly in tests rather than mocked.
 * The app settings system is internal application logic that benefits from integration testing.
 *
 * **Context Purpose**:
 * - Provides global application configuration (API URLs, region, account info)
 * - Manages app settings modal state for user preferences
 * - Centralizes environment-specific configuration
 * - Supports runtime configuration changes
 *
 * **Testing Setup**:
 * ```typescript
 * // RECOMMENDED: Use real AppSettingsProvider in tests
 * import { AppSettingsProvider } from '../context/AppSettingsProvider';
 *
 * const mockAppSettings = {
 *   apiUrl: 'https://test-api.example.com',
 *   stage: 'test',
 *   region: 'us-east-1',
 *   accountId: '123456789012',
 *   graphqlApiUrl: 'https://test-graphql.example.com'
 * };
 *
 * const renderWithAppSettings = (component) => {
 *   return render(
 *     <AppSettingsProvider value={mockAppSettings}>
 *       {component}
 *     </AppSettingsProvider>
 *   );
 * };
 *
 * // Test settings access and modal interactions
 * const { getByText } = renderWithAppSettings(<YourComponent />);
 * expect(getByText('us-east-1')).toBeInTheDocument();
 * ```
 *
 * **Why Use Directly**:
 * - Tests proper configuration propagation to components
 * - Verifies modal state management works correctly
 * - Ensures components handle different environment configurations
 * - Tests integration between settings and component behavior
 *
 * **External Dependencies to Mock**:
 * - None - this is pure React context logic
 *
 * **Modal State Management**:
 * The context also provides modal state management for the app settings dialog,
 * allowing components to open/close the settings modal programmatically.
 *
 * @see {@link useAppSettings} Hook for accessing application settings
 * @see {@link useAppSettingsModalState} Hook for managing settings modal
 */
export interface AppSettings {
  apiUrl: string;
  legacyApiUrl?: string;
  stage: string;
  region: string;
  accountId: string;
  rumApplicationId?: string;
  rumRoleArn?: string;
  graphqlApiUrl: string;
}

export const AppSettingsContext = React.createContext<AppSettings | null>(null);

/** Use the AppSettingsContext */
export function useAppSettings(): AppSettings {
  const context = React.useContext(AppSettingsContext);
  if (context === null) {
    throw new Error('useAppSettings must be used within a AppSettingsProvider');
  }
  return context;
}

export const AppSettingsModalStateContext = React.createContext<ModalState>([
  false,
  {
    open: () => {
      /* Intentionally Empty */
    },
    close: () => {
      /* Intentionally Empty */
    },
  },
]);

export function useAppSettingsModalState(): ModalState {
  return React.useContext(AppSettingsModalStateContext);
}
